// Rules for Shape Web Defender (backend key WORD_WEB_DEFENDER), ported from
// the iOS ShapeWeb game. Friendly spiders carry shapes and crawl in toward
// Zippy at the middle of the playground; tapping the matching shape in the
// tray shoos the closest spider carrying it. Spiders spawn faster and crawl
// quicker the longer the round runs, and a spider reaching Zippy costs a life
// (three lives per round).

// MARK: - Shapes

export const SHAPES = [
  { id: "circle", label: "Circle", color: "#FF6B78" },
  { id: "triangle", label: "Triangle", color: "#FFB53D" },
  { id: "square", label: "Square", color: "#4F9CE8" },
  { id: "star", label: "Star", color: "#FFD94D" },
  { id: "heart", label: "Heart", color: "#ED5CA8" },
  { id: "diamond", label: "Diamond", color: "#8266DB" },
];

export const SHAPE_IDS = SHAPES.map((shape) => shape.id);

export const shapeMeta = (id) => SHAPES.find((shape) => shape.id === id) || SHAPES[0];

// Fluffy spider sprite per palette slot (same order the iOS game uses so the
// same index picks a matching PNG). The PNGs are already colored, so the id
// picks both the body color name and the artwork.
export const SPIDER_SPRITES = [
  "swd_spider_purple",
  "swd_spider_coral",
  "swd_spider_teal",
  "swd_spider_orange",
  "swd_spider_blue",
  "swd_spider_berry",
];

// Matching vector body colors (fallback tinting) — same order as sprites.
export const BODY_COLORS = ["#7357D6", "#F06B73", "#2EAD91", "#E69B36", "#428CD9", "#B557A6"];

const BUBBLE_SCALES = [0.78, 1.0, 1.22];

// MARK: - Spider geometry

export const CENTER = { x: 50, y: 50 };
export const DANGER_PROGRESS = 0.9;
export const URGENT_PROGRESS = 0.67;
export const CLOSE_CALL_PROGRESS = 0.78;

// Straight-line crawl position (percentages of the play field).
export const spiderPosition = (spider) => ({
  x: spider.startX + (CENTER.x - spider.startX) * spider.progress,
  y: spider.startY + (CENTER.y - spider.startY) * spider.progress,
});

// On-screen position: the crawl plus a gentle side-to-side sway that fades out
// as the spider nears Zippy, so the crawl reads organic instead of mechanical.
export const spiderDisplayPosition = (spider) => {
  const pos = spiderPosition(spider);
  const dx = CENTER.x - spider.startX;
  const dy = CENTER.y - spider.startY;
  const length = Math.max(1, Math.sqrt(dx * dx + dy * dy));
  const sway = Math.sin(spider.progress * 16 + spider.id * 1.7) * 2.6 * (1 - spider.progress);
  return {
    x: pos.x + (-dy / length) * sway,
    y: pos.y + (dx / length) * sway,
  };
};

// Where a shooed spider swings away to: past its entry point, off-field.
export const spiderFleePoint = (spider) => ({
  x: spider.startX + (spider.startX - CENTER.x) * 0.45,
  y: spider.startY + (spider.startY - CENTER.y) * 0.45,
});

export const isUrgent = (spider) => spider.progress > URGENT_PROGRESS;

// MARK: - Adaptive difficulty tuning (ported from ShapeWebTuning.forLevel)

export const MIN_LEVEL = 1;
export const MAX_LEVEL = 3;
export const DEFAULT_LEVEL = 2; // a brand-new player starts in the middle

export const clampLevel = (level) => Math.max(MIN_LEVEL, Math.min(MAX_LEVEL, level));

export const tuningForLevel = (level) => {
  switch (clampLevel(level)) {
    case 1:
      return {
        level: 1,
        name: "Gentle",
        maxSpiders: 8,
        openingCount: 3,
        speedBase: 0.047,
        speedRamp: 0.0007,
        speedCap: 0.15,
        spawnBase: 3.6,
        spawnRamp: 0.02,
        spawnFloor: 1.9,
      };
    case 3:
      return {
        level: 3,
        name: "Challenge",
        maxSpiders: 13,
        openingCount: 5,
        speedBase: 0.076,
        speedRamp: 0.00115,
        speedCap: 0.24,
        spawnBase: 2.1,
        spawnRamp: 0.026,
        spawnFloor: 1.1,
      };
    default:
      return {
        level: 2,
        name: "Just Right",
        maxSpiders: 10,
        openingCount: 4,
        speedBase: 0.06,
        speedRamp: 0.00092,
        speedCap: 0.195,
        spawnBase: 2.8,
        spawnRamp: 0.023,
        spawnFloor: 1.5,
      };
  }
};

export const spawnInterval = (tuning, elapsed) =>
  Math.max(tuning.spawnFloor, tuning.spawnBase - elapsed * tuning.spawnRamp);

export const crawlSpeed = (tuning, elapsed) => {
  // A little per-spider variety so a wave doesn't crawl in lockstep.
  const jitter = Math.random() * (tuning.speedBase * 0.35);
  return Math.min(tuning.speedCap, tuning.speedBase + elapsed * tuning.speedRamp + jitter);
};

// MARK: - Spider factory

const randomFrom = (list) => list[Math.floor(Math.random() * list.length)];

export const makeSpider = (id, elapsed, tuning, angleOverride) => {
  const theta = angleOverride ?? Math.random() * Math.PI * 2;
  const paletteIndex = id % SPIDER_SPRITES.length;
  return {
    id,
    shape: randomFrom(SHAPE_IDS),
    bubbleScale: randomFrom(BUBBLE_SCALES),
    sprite: SPIDER_SPRITES[paletteIndex],
    color: BODY_COLORS[paletteIndex],
    startX: 50 + Math.cos(theta) * 57,
    startY: 50 + Math.sin(theta) * 63,
    progress: 0,
    speed: crawlSpeed(tuning, elapsed),
    // Once the round warms up, the occasional golden spider is worth double.
    isGolden: elapsed > 18 && Math.random() < 0.12,
  };
};

// Opening wave spread around the playground; count scales with level.
export const openingSpiders = (tuning) => {
  const angles = [-0.25, Math.PI * 0.82, Math.PI * 1.45];
  return Array.from({ length: tuning.openingCount }, (_, i) =>
    makeSpider(i + 1, 0, tuning, angles[i % angles.length])
  );
};

export const pointsForStreak = (streak) => 100 + Math.min(100, streak * 10);

// A finished round's rating for the adaptive system. ~22 defends reads as a
// confident 1.0; a short, few-defend round sits near 0.
export const roundRating = (defended) => Math.max(0, Math.min(1, defended / 22));

// Nudges the adaptive level from recent rounds. Two confident rounds in a row
// step up; two rough ones step down. Returns { level, ratings, change }.
export const applyDifficultyAfterRound = (level, ratings, defended) => {
  const rating = roundRating(defended);
  let nextRatings = [...ratings, rating].slice(-3);
  let nextLevel = level;
  let change = 0;
  const lastTwo = nextRatings.slice(-2);
  if (lastTwo.length === 2) {
    if (lastTwo.every((r) => r >= 0.99) && level < MAX_LEVEL) {
      nextLevel = level + 1;
      change = 1;
      nextRatings = [];
    } else if (lastTwo.every((r) => r <= 0.5) && level > MIN_LEVEL) {
      nextLevel = level - 1;
      change = -1;
      nextRatings = [];
    }
  }
  return { level: nextLevel, ratings: nextRatings, change };
};

// MARK: - Local persistence

const LEVEL_KEY = "swd_level";
const RATINGS_KEY = "swd_ratings";
const BEST_KEY = "swd_local_best";

export const readLevel = () => {
  try {
    const stored = Number(localStorage.getItem(LEVEL_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampLevel(stored) : DEFAULT_LEVEL;
  } catch {
    return DEFAULT_LEVEL;
  }
};

export const writeLevel = (level) => {
  try {
    localStorage.setItem(LEVEL_KEY, String(level));
  } catch {
    // Storage is optional.
  }
};

export const readRatings = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(RATINGS_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((r) => typeof r === "number").slice(-3) : [];
  } catch {
    return [];
  }
};

export const writeRatings = (ratings) => {
  try {
    localStorage.setItem(RATINGS_KEY, JSON.stringify(ratings.slice(-3)));
  } catch {
    // Storage is optional.
  }
};

export const readLocalBest = () => {
  try {
    const stored = Number(localStorage.getItem(BEST_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
};

export const writeLocalBest = (best) => {
  try {
    localStorage.setItem(BEST_KEY, String(best));
  } catch {
    // Storage is optional.
  }
};

export const timeLabel = (seconds) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
